import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'
import { LeaseAuditStatus } from '../../models/DocumentAudit/types'
import {
    LeaseAuditStep,
    RentRollParseConfig,
    RentRollReportIssueResponse,
    RentRollSummaryResponse,
} from './types'

export const getLeaseAuditStatus = (status: LeaseAuditStatus) => {
    if (status === 'REQUESTED') {
        return 'Requested'
    } else if (status === 'SETUP') {
        return 'Setup'
    } else if (status === 'WAITING_FOR_APPROVAL') {
        return 'Waiting for Approval'
    } else if (status === 'RUNNING_PIPELINE' || status === 'APPROVED') {
        return 'Processing'
    } else if (status === 'COMPLETED') {
        return 'Completed'
    } else if (status === 'REJECTED') {
        return 'Rejected'
    }
    return 'Unknown'
}

export const getLeaseAuditStep = (step: LeaseAuditStep) => {
    switch (step) {
        case 'CREATE':
            return 'Create'
        case 'RENT_ROLL_PROCESSING':
            return 'Rent Roll Processing'
        case 'ATTRIBUTE_SELECTION':
            return 'Attribute Selection'
        case 'DOCUMENT_UPLOAD':
            return 'Document Upload'
        case 'DOCUMENT_PARSING':
            return 'Document Parsing'
        case 'DOCUMENT_REVIEW':
            return 'Resident Terms'
        case 'DATA_EXTRACTION':
            return 'Data Extraction'
        case 'AUDITING':
            return 'Auditing'
        default:
            return 'Unknown'
    }
}

const rentRollBasePath = (auditId: number) =>
    `/lease-audit/${auditId}/rent-roll`

export const fetchRentRollSummary = (
    auditId: number,
): Promise<AxiosResponse<RentRollSummaryResponse>> =>
    axiosInstance.get(`${rentRollBasePath(auditId)}/summary/`)

const getFilenameFromContentDisposition = (
    contentDisposition: string | undefined,
): string | null => {
    if (!contentDisposition) {
        return null
    }
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
    return match ? match[1] : null
}

export const downloadRentRoll = async (
    auditId: number,
    fallbackFilename?: string,
): Promise<void> => {
    const response = await axiosInstance.get(
        `${rentRollBasePath(auditId)}/download/`,
        { responseType: 'blob' },
    )

    const filename =
        getFilenameFromContentDisposition(
            response.headers['content-disposition'],
        ) ??
        fallbackFilename ??
        'rent-roll.xlsx'

    const blob = new Blob([response.data], {
        type:
            response.headers['content-type'] ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
}

export const reportRentRollIssue = (
    auditId: number,
): Promise<AxiosResponse<RentRollReportIssueResponse>> =>
    axiosInstance.post(`${rentRollBasePath(auditId)}/report-issue/`)

export const updateRentRollParseConfig = (
    auditId: number,
    parseConfig: Partial<RentRollParseConfig>,
): Promise<AxiosResponse<RentRollParseConfig>> =>
    axiosInstance.patch(
        `${rentRollBasePath(auditId)}/parse-config/`,
        parseConfig,
    )

export interface ReparseRentRollResponse {
    task_id: string
    lease_file_audit_id: number
    message?: string
}

export const reparseRentRoll = (
    auditId: number,
): Promise<AxiosResponse<ReparseRentRollResponse>> =>
    axiosInstance.post(`${rentRollBasePath(auditId)}/parse/`)
